import { useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import TaskRow from "@/components/TaskRow";
import { Button } from "@/components/ui/button";
import { Trash2, Archive } from "lucide-react";
import { toast } from "sonner";
import { initials, todayISO } from "@/lib/tp";

const localDay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const daysLeft = (t) => {
  const base = t.trashed ? t.trashed_at || t.updated_at : t.completed_at || t.updated_at;
  if (!base) return 30;
  const ms = 30 * 86400000 - (Date.now() - new Date(base).getTime());
  return Math.max(0, Math.ceil(ms / 86400000));
};

export default function Cestino() {
  const { clients, rawTasks, deleteTask, updateTask } = useData();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [wipeOpen, setWipeOpen] = useState(false);

  // Cestino = task cestinate (eliminate) + task completate prima di oggi.
  // Entrambe restano 30 giorni, poi vengono eliminate automaticamente.
  const completed = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of rawTasks) {
      const isTrashed = !!t.trashed;
      const isCompletedBeforeToday =
        !t.trashed &&
        t.status === "completato" &&
        (() => {
          const day = localDay(t.completed_at || t.updated_at);
          return day && day < todayISO();
        })();
      if ((isTrashed || isCompletedBeforeToday) && !seen.has(t.id)) {
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [rawTasks]);

  // Group by client
  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of clients) map.set(c.id, []);
    for (const t of completed) {
      if (!map.has(t.client_id)) map.set(t.client_id, []);
      map.get(t.client_id).push(t);
    }
    return [...map.entries()]
      .map(([cid, list]) => ({
        client: clients.find((c) => c.id === cid) || { id: cid, name: "Cliente rimosso", color: "#52525B" },
        tasks: list,
      }))
      .filter((g) => g.tasks.length > 0);
  }, [completed, clients]);

  const restore = async (task) => {
    if (task.trashed) {
      await updateTask(task.id, { trashed: false });
    } else {
      // Task completata: torna attiva come "Da fare"
      await updateTask(task.id, { status: "da_fare" });
    }
    toast.success("Task ripristinata");
  };

  const wipeAll = async () => {
    if (!confirm(`Eliminare definitivamente ${completed.length} task dal cestino?`)) return;
    for (const t of completed) {
      // eslint-disable-next-line no-await-in-loop
      await deleteTask(t.id);
    }
    toast.success("Cestino svuotato");
    setWipeOpen(false);
  };

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Archivio</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1">
            Cestino
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Le task eliminate e quelle completate finiscono qui e restano 30 giorni, poi vengono
            eliminate automaticamente. Puoi ripristinarle o eliminarle subito.
          </p>
        </div>
        {isAdmin && completed.length > 0 && (
          <Button
            data-testid="wipe-cestino-button"
            onClick={wipeAll}
            className="h-10 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30"
          >
            <Trash2 size={14} className="mr-1.5" /> Svuota cestino
          </Button>
        )}
      </div>

      {completed.length === 0 ? (
        <div className="tp-glass rounded-3xl p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            <Archive className="text-zinc-400" />
          </div>
          <h3 className="text-xl font-semibold text-white">Cestino vuoto</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Le task che sposti nel cestino appariranno qui.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {grouped.map(({ client, tasks: list }) => (
            <section
              key={client.id}
              data-testid={`cestino-client-${client.id}`}
              className="tp-glass rounded-3xl overflow-hidden"
            >
              <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-xl flex items-center justify-center font-bold text-white text-xs overflow-hidden shrink-0"
                  style={{
                    backgroundColor: client.avatar_url ? "transparent" : client.color,
                    backgroundImage: client.avatar_url ? `url(${client.avatar_url})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {!client.avatar_url && initials(client.name)}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-white">{client.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {list.length} in archivio
                  </div>
                </div>
              </div>

              <div className="px-2 sm:px-3 py-2">
                {list.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`cestino-item-${t.id}`}
                    className="flex items-center gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <TaskRow task={t} inTrash />
                    </div>
                    <span
                      data-testid={`cestino-daysleft-${t.id}`}
                      title="Giorni prima dell'eliminazione definitiva"
                      className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 shrink-0 tabular-nums"
                    >
                      {daysLeft(t)}g
                    </span>
                    <button
                      onClick={() => restore(t)}
                      data-testid={`restore-task-${t.id}`}
                      className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 shrink-0"
                    >
                      Ripristina
                    </button>
                    {isAdmin && (
                      <button
                        onClick={async () => {
                          if (!confirm(`Eliminare definitivamente "${t.title}"?`)) return;
                          await deleteTask(t.id);
                          toast.success("Task eliminata");
                        }}
                        data-testid={`delete-task-${t.id}`}
                        title="Elimina definitivamente"
                        className="mr-2 p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
